import { useEffect, useState } from "react";
import type { DeletedTrip, LiveChange, PersonLocation } from "../../src/web/types.ts";
import { keepOpen } from "./event-stream.ts";

// Live updates: hears the trip's changes as they happen (server-sent events) and keeps those made
// after what's on screen, except what this person did on the web themselves. Nothing is applied: the
// page offers Refresh. `loaded` is the newest change on screen (the trip's last_change), so once the
// page reloads, what it now shows drops out by itself. Where people are isn't an edit: it arrives
// whole, as it changes, and replaces what the page had (null until the first one). `gone`: its owner
// deleted the trip while it was open (the stream then ends); `epoch` starts listening again (restored). Off (`on`
// false: a shared trip's view, which hears nothing live), nothing is heard.
export function useLive(
  trip: string,
  loaded: number,
  you: string,
  epoch = 0,
  on = true,
): { changes: LiveChange[]; locations: PersonLocation[] | null; gone: DeletedTrip | null } {
  const [heard, setHeard] = useState<LiveChange[]>([]);
  const [locations, setLocations] = useState<PersonLocation[] | null>(null);
  const [gone, setGone] = useState<DeletedTrip | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: one stream per trip, from what was first on screen
  useEffect(() => {
    setHeard([]);
    setLocations(null);
    setGone(null);
    if (!on) return;
    // Reconnecting (event-stream.ts), it picks up after the last change it got, so nothing is missed:
    // the browser sends that by itself, and a stream opened again asks for it.
    let last = loaded;
    return keepOpen(
      () => `/api/trips/${encodeURIComponent(trip)}/events?after=${last}`,
      (source, stop) => {
        source.addEventListener("change", (e) => {
          const change = JSON.parse((e as MessageEvent<string>).data) as LiveChange;
          last = Math.max(last, change.seq);
          if ("id" in change.by && change.by.id === you && change.via === "web") return;
          setHeard((h) => (h.some((c) => c.seq === change.seq) ? h : [...h, change]));
        });
        source.addEventListener("location", (e) => setLocations(JSON.parse((e as MessageEvent<string>).data)));
        source.addEventListener("gone", (e) => {
          stop();
          setGone(JSON.parse((e as MessageEvent<string>).data) as DeletedTrip);
        });
      },
    );
  }, [trip, you, epoch, on]);
  return { changes: heard.filter((c) => c.seq > loaded), locations, gone };
}
